import { Box, Paper, Typography, Tooltip, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiActivity, FiArrowDownLeft, FiArrowUpRight, FiCheckCircle, FiClock, FiFileText, FiRefreshCw, FiUsers, FiXCircle } from "react-icons/fi";
import { MainContainer } from "../../components/container/MainContainer";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import "../../style/pages-style/Engagement/engagement.css";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const Client_Directory_Reports = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [receivedReports, setReceivedReports] = useState([]);
  const [sentReports, setSentReports] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [receivers, setReceivers] = useState([]);
  const [senders, setSenders] = useState([]);

  useEffect(() => {
    Supplier_Reports.get_mine_received().then((res) => res.json()).then(setReceivedReports);
    Supplier_Reports.get_mine_sent().then((res) => res.json()).then(setSentReports);
  }, [refresh]);

  useEffect(() => {
    const temp = new Map();
    sentReports.forEach((report) => {
      if (temp.has(report.receiver_id)) {
        const ex = temp.get(report.receiver_id);
        ex.gwp += report.gwp || 0; ex.cost += report.cost || 0; ex.reports.push(report);
      } else {
        temp.set(report.receiver_id, {
          name: report.receiver_name, id: report.receiver_id,
          gwp: report.gwp || 0, cost: report.cost || 0, reports: [report],
        });
      }
    });
    setReceivers(Array.from(temp.values()));
  }, [sentReports]);

  useEffect(() => {
    const temp = new Map();
    receivedReports.forEach((report) => {
      if (temp.has(report.sender_id)) {
        const ex = temp.get(report.sender_id);
        ex.gwp += report.gwp || 0; ex.cost += report.cost || 0; ex.reports.push(report);
      } else {
        temp.set(report.sender_id, {
          name: report.sender_name, id: report.sender_id,
          gwp: report.gwp || 0, cost: report.cost || 0, reports: [report],
        });
      }
    });
    setSenders(Array.from(temp.values()));
  }, [receivedReports]);

  const stats = useMemo(() => [
    {
      title: "Connection Audit", items: [
        { label: "Active Clients", value: receivers.length, icon: FiUsers, color: "#2563eb", bg: "rgba(37, 99, 235, 0.08)" },
        { label: "Supplying Partners", value: senders.length, icon: FiArrowDownLeft, color: "#16a34a", bg: "rgba(22, 163, 74, 0.08)" },
        { label: "Total Reports Sent", value: sentReports.length, icon: FiArrowUpRight, color: "#7c3aed", bg: "rgba(124, 58, 237, 0.08)" },
        { label: "Total Reports Received", value: receivedReports.length, icon: FiFileText, color: "#0891b2", bg: "rgba(8, 145, 178, 0.08)" },
      ]
    },
    {
      title: "Impact & Financials", items: [
        { label: "GWP Shared (t)", value: (sentReports.reduce((s, r) => s + (r.gwp || 0), 0) / 1000).toFixed(2), icon: FiActivity, color: "#0891b2", bg: "rgba(8, 145, 178, 0.08)" },
        { label: "Cost Shared (₹)", value: sentReports.reduce((s, r) => s + (r.cost || 0), 0).toLocaleString(), icon: FiActivity, color: "#16a34a", bg: "rgba(22, 163, 74, 0.08)" },
        { label: "GWP Sourced (t)", value: (receivedReports.reduce((s, r) => s + (r.gwp || 0), 0) / 1000).toFixed(2), icon: FiActivity, color: "#2563eb", bg: "rgba(37, 99, 235, 0.08)" },
        { label: "Cost Sourced (₹)", value: receivedReports.reduce((s, r) => s + (r.cost || 0), 0).toLocaleString(), icon: FiActivity, color: "#d97706", bg: "rgba(217, 119, 6, 0.08)" },
      ]
    },
    {
      title: "Governance & Status", items: [
        { label: "Pending (Action Req)", value: receivedReports.filter(r => r.status === "Pending").length, icon: FiClock, color: "#d97706", bg: "rgba(217, 119, 6, 0.08)" },
        { label: "Accepted Records", value: receivedReports.filter(r => r.status === "Accepted").length, icon: FiCheckCircle, color: "#16a34a", bg: "rgba(22, 163, 74, 0.08)" },
        { label: "Rejected Records", value: receivedReports.filter(r => r.status === "Rejected").length, icon: FiXCircle, color: "#dc2626", bg: "rgba(220, 38, 38, 0.08)" },
      ]
    },
  ], [receivers, senders, sentReports, receivedReports]);

  return (
    <MainContainer>
      {/* ── Metric Sections ────────────────────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: ORG_PALETTE.spacing.pageGap }}>
        {stats.map((section, sIdx) => (
          <Box key={sIdx} sx={{ animation: `eng-fadeInUp 0.4s ease both`, animationDelay: `${sIdx * 0.1}s` }}>
            <Typography variant="Table Title" sx={{ mb: 2.5, pl: 1 }}>
              {t(section.title)}
            </Typography>
            <Box className="eng-flex-grid" sx={{ gap: ORG_PALETTE.spacing.metricCardGap }}>
              {section.items.map((m, mIdx) => {
                const Icon = m.icon;
                return (
                  <Box className="eng-metric-item" key={mIdx}>
                    <Paper elevation={0} sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2.5, borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: m.bg }}>
                        <Icon size={20} color={m.color} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                        <Typography sx={{ fontFamily: "Poppins", fontSize: 22, fontWeight: 800, color: tok.text.primary, lineHeight: 1.1 }}>
                          {m.value}
                        </Typography>
                        <Typography sx={{ fontFamily: "Poppins", fontSize: 13, color: tok.text.chartSub, mt: 0.5, fontWeight: 500 }}>
                          {t(m.label)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </MainContainer>
  );
};

export default Client_Directory_Reports;
